import { useTranslation } from 'react-i18next'

import { Alert, Box, TertiaryButton } from '@enechain/ecloud-designsystem'
import { FaArrowRightRegular } from '@enechain/ecloud-icons'

import { AlertActions } from '~/components/common/AlertActions'
import { iconSize } from '~/constants/iconSize'
import type { BidFormAlerts as Alerts } from '~/pages/SpotBidding/models/bidFormAlerts'
import type { JepxLimitViolationKind } from '~/pages/SpotBidding/models/spotBidValidation'

/** CTA が focus する入力欄。 */
export type BidFormField = 'price' | 'volume'

type Props = {
  alerts: Alerts
  /** CTA 押下でその入力欄へ focus + 全選択する。 */
  onFocusField: (field: BidFormField) => void
  /**
   * 次アクション CTA を出さない (= 表示専用モーダル)。 直せない画面で「修正」 を
   * 出しても押した先で何もできないため、 警告の本文だけ残す。
   */
  hideActions?: boolean
}

/**
 * JEPX 制度上限違反の種別ごとの文言・誘導先。
 * 誘導先の入力欄は design `spot.html` の `_renderBnewRowAlert` が持つ 3 分岐
 * (price-range / min-unit / max-volume) と同じ割り当て (= 価格 / 数量)。
 * 文言は値域 2 種 + 刻み 2 種の 4 群へ写す。
 *
 * **値域の 2 文言は `biddingCommon.validation.*` を引く** — spec §9 AC-BSP-078 が
 * 価格範囲・入札量範囲を「両画面 (スポット / 時間前) 共通の入力チェック文言」と
 * 定めるため、 spot 固有の namespace に言い換えを置かない。 刻み違反 (0.01 円/kWh・
 * 0.1 MW) は AC-BSP-078 の規定外なので spot 側の文言のまま残す (値域文言へ丸めると
 * 何を直せばよいかが分からなくなる)。
 * key は動的組み立てにせず実キーを書き出す (= i18n key の grep / 未使用検出を効かせる)。
 */
const JEPX_MESSAGE: Record<
  JepxLimitViolationKind,
  { messageKey: string; field: BidFormField }
> = {
  'price-min': {
    messageKey: 'page:biddingCommon.validation.priceRange',
    field: 'price',
  },
  'price-max': {
    messageKey: 'page:biddingCommon.validation.priceRange',
    field: 'price',
  },
  'price-step': {
    messageKey: 'page:biddingSpot.formAlert.limitViolation.priceStep',
    field: 'price',
  },
  'volume-min': {
    messageKey: 'page:biddingCommon.validation.volumeRange',
    field: 'volume',
  },
  'volume-max': {
    messageKey: 'page:biddingCommon.validation.volumeRange',
    field: 'volume',
  },
  'volume-step': {
    messageKey: 'page:biddingSpot.formAlert.limitViolation.volumeStep',
    field: 'volume',
  },
}

/**
 * 札の個別作成 / 編集モーダル冒頭に出す警告と「次アクション」 CTA。
 *
 * design SoT: `design/bidding/spot.html` の `#bnew-row-alert` /
 * `_renderBnewRowAlert`。 出す順・トーン・CTA の有無を写す:
 *
 * - **入札制限違反 (negative) が先、 売買逆転 (warning) が後**。 前者は承認申請前に
 *   直す必要があり、 後者は「意図的な逆転もあり得る」 ため止めない (spec AC-BSP-009)。
 * - 各 alert は右端に CTA を持ち、 押すと直すべき入力欄へ focus + 全選択する。
 *   「何が悪いか」だけ出して直す場所を示さないと、 モーダルが縦に長い時に迷うため。
 *
 * 両方該当する場合は 2 枚とも出す (一覧の警告アイコンが 2 種を同時に出すのと同じ考え)。
 */
export const BidFormAlerts = ({
  alerts,
  onFocusField,
  hideActions = false,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()
  const { isInversion, jepxViolationKinds } = alerts

  // 同じ入力欄に対する違反が複数出ても alert は 1 枚にまとめる
  // (価格の上下限と刻みが同時に出ることは無いが、 価格と数量は同時に出る)。
  const firstByField = new Map<BidFormField, JepxLimitViolationKind>()
  for (const kind of jepxViolationKinds) {
    const { field } = JEPX_MESSAGE[kind]
    if (!firstByField.has(field)) firstByField.set(field, kind)
  }

  if (!isInversion && firstByField.size === 0) return null

  return (
    <Box flexDirection="column" gap={2} display="flex">
      {[...firstByField.entries()].map(([field, kind]) => (
        <Alert
          key={field}
          description={
            <>
              {t(JEPX_MESSAGE[kind].messageKey)}{' '}
              {t('page:biddingSpot.formAlert.limitViolation.suffix')}
            </>
          }
          status="error"
          title={t('page:biddingSpot.formAlert.limitViolation.title')}
        >
          {!hideActions && (
            <AlertActions>
              <TertiaryButton
                onClick={() => {
                  onFocusField(field)
                }}
                size="sm"
              >
                {t(`page:biddingSpot.formAlert.action.${field}`)}
                <FaArrowRightRegular boxSize={iconSize.gridInline} />
              </TertiaryButton>
            </AlertActions>
          )}
        </Alert>
      ))}

      {isInversion && (
        <Alert
          description={t('page:biddingSpot.formAlert.inversion.description')}
          status="warning"
          title={t('page:biddingSpot.formAlert.inversion.title')}
        >
          {!hideActions && (
            <AlertActions>
              <TertiaryButton
                onClick={() => {
                  onFocusField('price')
                }}
                size="sm"
              >
                {t('page:biddingSpot.formAlert.action.reviewPrice')}
                <FaArrowRightRegular boxSize={iconSize.gridInline} />
              </TertiaryButton>
            </AlertActions>
          )}
        </Alert>
      )}
    </Box>
  )
}
